import { z } from 'zod'

/**
 * Identifier for an authored object. Stable across edits: rules, events and
 * diagnostics all reference objects by id, so renaming an emitter must never
 * change it.
 */
export const Id = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/)
export type Id = z.infer<typeof Id>

/**
 * A point in the world's local East-North-Up frame, in metres, with the origin
 * at the world anchor. All authored geometry is stored this way and converted
 * to geographic coordinates through the active placement — that separation is
 * what lets one world be placed on any football field on Earth.
 */
export const Vec2 = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
})
export type Vec2 = z.infer<typeof Vec2>

export const Vec3 = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  z: z.number().finite(),
})
export type Vec3 = z.infer<typeof Vec3>

export const LatLon = z.object({
  lat: z.number().finite().min(-90).max(90),
  lon: z.number().finite().min(-180).max(180),
})
export type LatLon = z.infer<typeof LatLon>

/** Gain in decibels. The floor is treated as silence by the engine. */
export const GainDb = z.number().finite().min(-80).max(12)

/** Degrees clockwise from true north. */
export const Bearing = z.number().finite().min(0).lt(360)

export const Milliseconds = z.number().finite().nonnegative()
export const Metres = z.number().finite().nonnegative()
export const MetresPerSecond = z.number().finite().nonnegative()

export const Comparison = z.enum(['eq', 'neq', 'lt', 'lte', 'gt', 'gte'])
export type Comparison = z.infer<typeof Comparison>

export const VariableValue = z.union([z.number().finite(), z.boolean(), z.string().max(256)])
export type VariableValue = z.infer<typeof VariableValue>

/** Fade shapes. `equalPower` is the correct default for crossfades. */
export const FadeCurve = z.enum(['linear', 'exponential', 'equalPower'])
export type FadeCurve = z.infer<typeof FadeCurve>

/**
 * Whether a piece of runtime state belongs to one listener or to the whole
 * session. Scope is per-rule and per-variable rather than per-world, so a
 * shared world can hold private narration and a communal thunderclap at once.
 */
export const Scope = z.enum(['listener', 'session'])
export type Scope = z.infer<typeof Scope>
